import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Config } from "../config";

const baseUrl = Config.serviceUrls.officeBaseUrl;
const userBaseUrl = Config.serviceUrls.userBaseUrl;
const UTILITY_POINT_URL = Config.serviceUrls.utilityBaseUrl;

export async function getOffices(
  userId,
  pageNumber,
  pageSize,
  activeStatus = null,
) {
  const response = await axios.get(`${baseUrl}/Office/Owner/${userId}`, {
    params: {
      pageNumber,
      pageSize,
      activeStatus,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return {
    items: response.data.data.office_list,
    pagination: response.data.pagination,
  };
}

export function useOffices(
  userId,
  pageNumber,
  pageSize,
  activeStatus = null,
  options = {},
) {
  return useQuery(
    ["/Office/Owner", userId, pageNumber, pageSize, activeStatus],
    () => getOffices(userId, pageNumber, pageSize, activeStatus),
    options,
  );
}

export async function getOfficesByUserId(
  userId,
  pageNumber,
  pageSize,
  searchTerm,
) {
  const response = await axios.post(`${baseUrl}/Office/ByUser/${userId}`, {
    pageNumber,
    pageSize,
    searchTerm,
  });
  return response.data;
}

export function useOfficesByUserId(userId, pageNumber, pageSize, searchTerm) {
  return useQuery(
    ["officesByUserId", userId, pageNumber, pageSize, searchTerm],
    () => getOfficesByUserId(userId, pageNumber, pageSize, searchTerm),
  );
}

/**
 * Offices a user belongs to for a given owner (scheduler drag-drop office picker).
 * @param {number|string} ownerId
 * @param {number|string} userId
 * @returns {Promise<Array<{ id: number, name: string, officeImage?: string }>>}
 */
export async function getOfficesByOwnerAndUser(ownerId, userId) {
  const response = await axios.get(`${baseUrl}/Office/byOwnerAndUser`, {
    params: {
      OwnerId: ownerId,
      UserId: userId,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data?.data || [];
}

export async function getStaff(
  officeId,
  pageNumber,
  pageSize,
  searchTerm,
  isActive,
) {
  const response = await axios.post(`${baseUrl}/Office/${officeId}/staffList`, {
    pageNumber,
    pageSize,
    searchTerm,
    isActive,
  });
  return response.data;
}

export function useStaff(officeId, pageNumber, pageSize, searchTerm, isActive) {
  return useQuery(
    ["staff", officeId, pageNumber, pageSize, searchTerm, isActive],
    () => getStaff(officeId, pageNumber, pageSize, searchTerm, isActive),
  );
}

export async function getOfficeDetail(officeId) {
  const response = await axios.get(`${baseUrl}/Office/Details`, {
    params: {
      officeId,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data.data;
}

export function useOfficeDetail(officeId, options = {}) {
  return useQuery(
    ["Office/Details", officeId],
    () => getOfficeDetail(officeId),
    options,
  );
}
export async function getOfficeStaffInformationList({
  officeId,
  pageNumber,
  pageSize,
  adminList,
}) {
  const response = await axios.get(`${baseUrl}/Office/AllMembers`, {
    params: {
      officeId,
      pageNumber,
      pageSize,
      adminList,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export function useGetOfficeStaffInformationList(
  { officeId, pageNumber, pageSize, adminList },
  options = {},
) {
  return useQuery(
    ["Office/Details", officeId, pageNumber, pageSize, adminList],
    () =>
      getOfficeStaffInformationList({
        officeId,
        pageNumber,
        pageSize,
        adminList,
      }),
    options,
  );
}
export async function getOfficesInformationList({
  pageNumber,
  pageSize,
  ownerId,
  sortBy,
  SortDescending,
  SearchTerm,
  officeIds,
}) {
  const response = await axios.get(
    `${baseUrl}/Office/GetOfficesFromDirectory?${officeIds.reduce(
      (prev, curr) => {
        prev += "&officeIds=" + curr;
        return prev;
      },
      "",
    )}`,
    {
      params: {
        pageNumber,
        pageSize,
        ownerId,
        sortBy,
        SortDescending,
        SearchTerm,
      },
    },
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export function useGetOfficesInformationList(
  {
    pageNumber,
    pageSize,
    ownerId,
    sortBy,
    SortDescending,
    SearchTerm,
    officeIds,
  },
  options = {},
) {
  return useQuery(
    [
      "Office/GetOfficesFromDirectory",
      pageNumber,
      pageSize,
      ownerId,
      sortBy,
      SortDescending,
      SearchTerm,
      officeIds,
    ],
    () =>
      getOfficesInformationList({
        pageNumber,
        pageSize,
        ownerId,
        sortBy,
        SortDescending,
        SearchTerm,
        officeIds,
      }),
    options,
  );
}

export async function getGlobalOfficeStaffInformationList({
  officeIds,
  pageNumber,
  pageSize,
  SearchTerm,
  isAdminOnly,
  ownerId,
  sortBy,
  SortDescending,
  designationIds,
}) {
  const response = await axios.get(
    `${userBaseUrl}/Staff/GetAllStaffByOwnerId?${officeIds.reduce(
      (prev, curr) => {
        prev += "&officeIds=" + curr;
        return prev;
      },
      "",
    )}${designationIds.reduce((prev, curr) => {
      prev += "&designationIds=" + curr;
      return prev;
    }, "")}`,
    {
      params: {
        ownerId,
        pageNumber,
        pageSize,
        SearchTerm,
        isAdminOnly,
        sortBy,
        SortDescending,
      },
    },
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export function useGetGlobalOfficeStaffInformationList(
  {
    ownerId,
    officeIds,
    pageNumber,
    pageSize,
    SearchTerm,
    isAdminOnly,
    sortBy,
    SortDescending,
    designationIds,
  },
  options = {},
) {
  return useQuery(
    [
      "Staff/GetAllStaffByOwnerId",
      ownerId,
      officeIds,
      pageNumber,
      pageSize,
      SearchTerm,
      isAdminOnly,
      sortBy,
      SortDescending,
      designationIds,
    ],
    () =>
      getGlobalOfficeStaffInformationList({
        ownerId,
        officeIds,
        pageNumber,
        pageSize,
        SearchTerm,
        isAdminOnly,
        sortBy,
        SortDescending,
        designationIds,
      }),
    options,
  );
}

export async function updateTimesheetAndNotificationPreferences(
  officeId,
  isTimesheetPreferenceTypeOnPremises,
  isTypeStaticIP,
  isTypeGeocoordinate,
  isCovidFormFillNotificationOn,
  isTimesheetFillNotificationOn,
  isTimesheetFromMobileStaticIPAllowed,
  isTypeNfc,
) {
  const response = await axios.put(
    `${baseUrl}/Office/${officeId}/updateOnPremisesAndNotificationPreferences`,
    {},
    {
      params: {
        isTimesheetPreferenceTypeOnPremises,
        isTypeStaticIP,
        isTypeGeocoordinate,
        isCovidFormFillNotificationOn,
        isTimesheetFillNotificationOn,
        isTimesheetFromMobileStaticIPAllowed,
        isTypeNfc,
      },
    },
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data?.message;
}

export function useUpdateTimesheetAndNotificationPreferenceMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      officeId,
      isTimesheetPreferenceTypeOnPremises,
      isTypeStaticIP,
      isTypeGeocoordinate,
      isCovidFormFillNotificationOn,
      isTimesheetFillNotificationOn,
      isTimesheetFromMobileStaticIPAllowed,
      isTypeNfc,
    }) =>
      updateTimesheetAndNotificationPreferences(
        officeId,
        isTimesheetPreferenceTypeOnPremises,
        isTypeStaticIP,
        isTypeGeocoordinate,
        isCovidFormFillNotificationOn,
        isTimesheetFillNotificationOn,
        isTimesheetFromMobileStaticIPAllowed,
        isTypeNfc,
      ),
    {
      onSuccess: (data, variables) =>
        queryClient.invalidateQueries(["Office/Details", variables.officeId]),
    },
  );
}

export async function updateTimesheetSubmissionPreferences(
  officeId,
  IsTimesheetEnabledForShift,
) {
  const response = await axios.put(
    `${baseUrl}/Office/UpdateIsTimesheetEnabledForShift?officeId=${officeId}&IsTimesheetEnabledForShift=${IsTimesheetEnabledForShift}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export function useUpdateTimesheetSubmissionMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ officeId, IsTimesheetEnabledForShift }) =>
      updateTimesheetSubmissionPreferences(
        officeId,
        IsTimesheetEnabledForShift,
      ),
    {
      onSuccess: (data, variables) =>
        queryClient.invalidateQueries([
          "Office/UpdateIsTimesheetEnabledForShift",
          variables.officeId,
          variables.IsTimesheetEnabledForShift,
        ]),
    },
  );
}

export async function updateTimesheetSubmissionSelectedStaff(
  OfficeId,
  ShiftTimesheetEnabledUserList,
) {
  const response = await axios.post(
    `${baseUrl}/Office/AddShiftTimesheetEnabledUser`,
    { OfficeId, ShiftTimesheetEnabledUserList },
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export async function updateOfficeSetupManagementViewHide(OfficeId) {
  const response = await axios.patch(
    `${baseUrl}/Office/NeverShowPmsConnectBar`,
    { OfficeId },
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export function useUpdateTimesheetSubmissionSelectedStaffMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ officeId, ShiftTimesheetEnabledUserList }) =>
      updateTimesheetSubmissionSelectedStaff(
        officeId,
        ShiftTimesheetEnabledUserList,
      ),
    {
      onSuccess: (data, variables) =>
        queryClient.invalidateQueries([
          "Office/AddShiftTimesheetEnabledUser",
          variables.officeId,
          variables.ShiftTimesheetEnabledUserList,
        ]),
    },
  );
}

export async function getOfficeDetailForStaff(officeId) {
  const req1 = axios.get(`${baseUrl}/Office/Details`, { params: { officeId } });
  const req2 = axios.get(`${baseUrl}/Office/${officeId}`);

  var [res1, res2] = await Promise.all([req1, req2]);

  if (res1.data.statusCode !== 200) {
    throw new Error(res1.data.message);
  }

  if (res2.data.statusCode !== 200) {
    throw new Error(res2.data.message);
  }

  return {
    ...res1.data.data,
    ...res2.data.data,
  };
}

export function useOfficeDetailForStaff(officeId) {
  return useQuery(["officeDetailForStaff", officeId], () =>
    getOfficeDetailForStaff(officeId),
  );
}
export async function getOfficeTimesheetSubmissionSelectedStaff(officeId) {
  const res = await axios.get(
    `${baseUrl}/Office/GetShiftTimesheetEnabledUser?officeId=${officeId}`,
  );

  if (res.data.statusCode !== 200) {
    throw new Error(res.data.message);
  }

  return {
    ...res.data,
  };
}

export function useOfficeTimesheetSubmissionSelectedStaff(officeId) {
  return useQuery(["Office/GetShiftTimesheetEnabledUser", officeId], () =>
    getOfficeTimesheetSubmissionSelectedStaff(officeId),
  );
}

export async function updateClockInClockOutPreferences(
  officeId,
  isClockInOutOn,
) {
  const response = await axios.put(
    `${baseUrl}/Office/ClockInOut`,
    {},
    {
      params: {
        officeId,
        isClockInOutOn,
      },
    },
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data?.message;
}

export function useUpdateClockInClockOutPreferenceMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ officeId, isClockInOutOn }) =>
      updateClockInClockOutPreferences(officeId, isClockInOutOn),
    {
      onSuccess: (data, variables) =>
        queryClient.invalidateQueries(["Office/Details", variables.officeId]),
    },
  );
}

export const getCityList = async (provienceId) => {
  let res = await axios.get(`${UTILITY_POINT_URL}/Utility/City/${provienceId}`);

  if (res.data.statusCode !== 200) {
    throw new Error(res.data.message);
  }

  return res.data.data;
};

export const getProvinceList = async (countryId) => {
  let res = await axios.get(
    `${UTILITY_POINT_URL}/Utility/Province/${countryId}`,
  );

  if (res.data.statusCode !== 200) {
    throw new Error(res.data.message);
  }

  return res.data.data;
};

export const useGetProviceList = (countryId, options = {}) => {
  return useQuery(
    ["provice-list", countryId],
    () => getProvinceList(countryId),
    options,
  );
};

export async function getApprovalsAndRequests(
  officeId,
  ownerId,
  pageNo,
  pageSize,
) {
  const response = await axios.get(`${baseUrl}/Office/RecentUpdates`, {
    params: {
      officeId,
      pageNo,
      pageSize,
      ownerId,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export function useApprovalsAndRequests(
  officeId,
  ownerId,
  pageNo,
  pageSize,
  options = {},
) {
  return useQuery(
    ["Office/ApprovalAndRequest", officeId],
    () => getApprovalsAndRequests(officeId, ownerId, pageNo, pageSize),
    options,
  );
}

export const getWarningDetails = async (officeId) => {
  let res = await axios.get(`${baseUrl}/Office/Preferences/${officeId}`);

  if (res.data.statusCode !== 200) {
    throw new Error(res.data.message);
  }

  return res.data.data;
};

export const useWarningDetails = (officeId, options = {}) => {
  return useQuery(
    ["warning-detail", officeId],
    () => getWarningDetails(officeId),
    options,
  );
};

export const removeDeactivatedOffice = async (params) => {
  const response = await axios.post(`${baseUrl}/Office/leaveOffice`, params);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

export const getSyncedDoctorAndPatient = async (officeId) => {
  let res = await axios.get(
    `${baseUrl}/Sikka/SyncDoctorAndPatient?OfficeId=${officeId}`,
  );

  if (res.data.statusCode !== 200) {
    throw new Error(res.data.message);
  }

  return res.data;
};

export async function getOfficeListingBySikkaConnectedStatus(
  OwnerId,
  PageNo,
  PageSize,
  IsSikkaConnected = false,
) {
  const response = await axios.get(`${baseUrl}/Office/SikkaConnected`, {
    params: {
      OwnerId,
      PageNo,
      PageSize,
      IsSikkaConnected,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return {
    items: response.data.data,
    pagination: response.data.pagination,
  };
}

export function useOfficeListingBySikkaConnectedStatus(
  OwnerId,
  PageNo,
  PageSize,
  IsSikkaConnected = false,
  options = {},
) {
  return useQuery(
    ["/Office/SikkaConnected", OwnerId, PageNo, PageSize, IsSikkaConnected],
    () =>
      getOfficeListingBySikkaConnectedStatus(
        OwnerId,
        PageNo,
        PageSize,
        IsSikkaConnected,
      ),
    options,
  );
}

export const getOfficeById = async (officeId) => {
  const res = await axios.get(`${baseUrl}/Office/${officeId}`);
  if (res.data.statusCode !== 200) {
    throw new Error(res.data.message);
  }

  return res.data.data;
};

export function useOfficeById(officeId, options = {}) {
  return useQuery(["/office/id", officeId], () => getOfficeById(officeId), {
    enabled: !!officeId && !isNaN(officeId),
    ...options,
  });
}

export async function getStaffByUserId(officeId, userId) {
  const response = await axios.get(`${userBaseUrl}/Staff/ByUserIdAndOfficeId`, {
    params: {
      officeId,
      userId,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data.data;
}

export function useStaffByUserId(officeId, userId, options = {}) {
  return useQuery(
    ["staff/Details", officeId, userId],
    () => getStaffByUserId(officeId, userId),
    options,
  );
}

export async function getSikkaOperatory(officeId) {
  const response = await axios.get(
    `${baseUrl}/Sikka/OperatoryList?OfficeId=${officeId}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data.data;
}

export function useSikkaOperatory(officeId) {
  return useQuery(["staff/Details", officeId], () =>
    getSikkaOperatory(officeId),
  );
}

export async function getOfficeInvitations(
  officeId,
  userId,
  inviteStatus,
  searchTerm,
  pageNo,
  pageSize,
  sortBy,
  sortOrder,
) {
  let staffBaseUrl = `${baseUrl}/Office/Invitations?userId=${userId}&inviteStatus=${inviteStatus}&pageNo=${pageNo}&pageSize=${pageSize}`;
  const queryParams = [];
  if (officeId) {
    queryParams.push(`officeId=${officeId}`);
  }
  if (searchTerm) {
    queryParams.push(`searchTerm=${searchTerm}`);
  }
  if (sortBy) {
    queryParams.push(`sortBy=${sortBy}`);
  }
  if (sortOrder) {
    queryParams.push(`sortOrder=${sortOrder}`);
  }
  if (queryParams.length > 0) {
    staffBaseUrl += `${queryParams.join("&")}`;
  }

  const response = await axios.get(staffBaseUrl);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return {
    items: response.data.data?.invitations_list,
  };
}

export function useOfficeInvitations(
  officeId,
  userId,
  inviteStatus,
  searchTerm,
  pageNo,
  pageSize,
  sortBy,
  sortOrder,
) {
  return useQuery(
    [
      "/Office/Invitations",
      officeId,
      userId,
      inviteStatus,
      searchTerm,
      pageNo,
      pageSize,
      sortBy,
      sortOrder,
    ],
    () =>
      getOfficeInvitations(
        officeId,
        userId,
        inviteStatus,
        searchTerm,
        pageNo,
        pageSize,
        sortBy,
        sortOrder,
      ),
  );
}

export async function OfficeInvitationsActions(params) {
  const response = await axios.post(`${userBaseUrl}/Staff/Invite`, params);

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function getDashboardApprovalsAndRequests(
  ownerId,
  pageNo,
  pageSize,
  userIds = [],
  officeIds = [],
) {
  const response = await axios.post(
    `${baseUrl}/Office/DashboardRecentUpdates`,
    {
      ownerId,
      pageNo,
      pageSize,
      userIds: userIds,
      officeIds: officeIds,
    },
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export function useDashboardApprovalsAndRequests(
  ownerId,
  pageNo,
  pageSize,
  userIds,
  officeIds,
  options = {},
) {
  return useQuery(
    [
      "Office/ApprovalAndRequest",
      ownerId,
      pageNo,
      pageSize,
      userIds,
      officeIds,
    ],
    () =>
      getDashboardApprovalsAndRequests(
        ownerId,
        pageNo,
        pageSize,
        userIds,
        officeIds,
      ),
    options,
  );
}

export async function getFiscalYear(officeId) {
  const response = await axios.get(
    `${baseUrl}/Office/GetFiscalYear/${officeId}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export function useGetFiscalYear(officeId, options = {}) {
  return useQuery(
    ["/Office/GetFiscalYear", officeId],
    () => getFiscalYear(officeId),
    options,
  );
}

export async function addFiscalYear(params) {
  const response = await axios.post(`${baseUrl}/Office/AddFiscalYear`, params);

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function getGetOfficeEmailBranding() {
  const response = await axios.get(`${baseUrl}/Office/GetOfficeEmailBranding`);

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data?.data;
}

export function useGetOfficeEmailBranding(ownerId, options = {}) {
  return useQuery(
    ["Office/GetOfficeEmailBranding", ownerId],
    () => getGetOfficeEmailBranding(ownerId),
    options,
  );
}

export async function addOfficeEmailBranding(params) {
  const response = await axios.post(
    `${baseUrl}/Office/AddOfficeEmailBranding`,
    params,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export const getUniqueStaffsUnderOwner = async (ownerId) => {
  const response = await axios.get(
    `${userBaseUrl}/Staff/GetDistinctUsersByOwnerId?ownerId=${ownerId}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response?.data;
};
export function useGetUniqueStaffsUnderOwner(ownerId, options = {}) {
  return useQuery(
    ["Staff/GetDistinctUsersByOwnerId", ownerId],
    () => getUniqueStaffsUnderOwner(ownerId),
    options,
  );
}

export async function getAdminStaffOfficeList(ownerId) {
  const response = await axios.get(
    `${baseUrl}/Office/GetOfficeByOwnerAndOfficeId?ownerId=${ownerId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}
export function useGetAdminStaffOfficeList(ownerId, options = {}) {
  return useQuery(
    ["Office/GetOfficeByOwnerAndOfficeId", ownerId],
    () => getAdminStaffOfficeList(ownerId),
    options,
  );
}
